import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { ExclamationTriangleIcon, HomeIcon, ArrowRightIcon } from '@heroicons/react/24/outline'

const NotFoundPage = () => {
  return (
    <>
      <Helmet>
        <title>Page Not Found - Nexus Global Solutions</title>
        <meta name="description" content="The page you're looking for doesn't exist. Return to Nexus Global Solutions homepage." />
      </Helmet>
      
      <section className="corporate-section bg-white min-h-screen flex items-center">
        <div className="corporate-container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-accent-800 to-gold-600 rounded-full mx-auto mb-8 flex items-center justify-center">
              <ExclamationTriangleIcon className="h-12 w-12 text-white" />
            </div>
            
            <h1 className="font-display text-6xl font-bold text-primary-900 mb-4">
              404
            </h1>
            
            <h2 className="font-display text-2xl font-semibold text-primary-800 mb-6">
              Page Not Found
            </h2>
            
            <p className="text-lg text-primary-700 mb-8 leading-relaxed">
              The page you're looking for doesn't exist or has been moved. 
              Let's get you back to exploring our international business solutions.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/" className="btn-primary inline-flex items-center justify-center space-x-2">
                <HomeIcon className="h-4 w-4" />
                <span>Return Home</span>
              </Link>
              <Link to="/contact" className="btn-outline inline-flex items-center justify-center space-x-2">
                <span>Contact Support</span>
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
            
            <div className="mt-12 text-sm text-primary-600">
              <p>Looking for something specific? Try these popular pages:</p>
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                <Link to="/about" className="text-accent-800 hover:text-accent-900 transition-colors">About Us</Link>
                <Link to="/employees" className="text-accent-800 hover:text-accent-900 transition-colors">Our Team</Link>
                <Link to="/news" className="text-accent-800 hover:text-accent-900 transition-colors">News</Link>
                <Link to="/careers" className="text-accent-800 hover:text-accent-900 transition-colors">Careers</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="educational-data" style={{ display: 'none' }}>
        <div data-page="404" data-content-type="error-page">
          <span data-error-code="404">Page Not Found</span>
          <span data-error-message="The requested page does not exist">Error Message</span>
        </div>
        
        {/* OSINT Easter Egg: Hint about hidden pages */}
        <div data-hidden-pages="true">
          {/* These pages exist but are not in the navigation */}
          <span data-hidden-path="/admin/login">Admin Login Portal</span>
          <span data-hidden-path="/admin/dashboard">Admin Dashboard</span>
          <span data-hidden-path="/api/docs/swagger">API Documentation (Internal)</span>
          <span data-hidden-path="/legacy-admin-2023">Legacy Admin Panel (Deprecated)</span>
          <span data-hidden-path="/employee-portal">Employee Self-Service Portal</span>
          <span data-hidden-path="/internal/reports">Internal Reports Dashboard</span>
        </div>

        {/* Developer breadcrumb left in the error template. Rendered into the DOM
            (not just a source comment) so it survives production minification. */}
        <div data-debug-note="error-page-intelligence">
          <span data-base64-hint="L2FwaS9pbnRlcm5hbC9zZWNyZXRzP2FwaV9rZXk9TkdTLUFQSS1URVNULTIwMjQteEs5bVAybkw=">
            Base64 hint: L2FwaS9pbnRlcm5hbC9zZWNyZXRzP2FwaV9rZXk9TkdTLUFQSS1URVNULTIwMjQteEs5bVAybkw=
          </span>
        </div>

        {/* 
          DEVELOPER NOTE: These paths return 404 for non-authenticated users
          but the pages actually exist. Check robots.txt for hints.
          
          Hidden endpoint discovery:
          - /api/internal/ - requires API key header
          - /api/debug/config - requires debug=true parameter
          - /api/v2/news/:slug?include_clues=true - reveals investigation data
          
          Contact IT for access: j.chen@nexusglobal-consulting.com
        */}
      </div>

      {/* 
        ============================================================
        404 PAGE EASTER EGG
        ============================================================
        
        If you're seeing this, you're looking in the right places!
        
        Some paths that definitely DON'T exist (wink wink):
        - /admin/dev-access
        - /api/backup/users
        - /api/internal/employees
        - /debug/client-info
        
        Error codes to investigate:
        - ERR_NGS_AUTH_001: Invalid credentials
        - ERR_NGS_AUTH_002: Session expired
        - ERR_NGS_ACCESS_001: Insufficient permissions
        - ERR_NGS_INTERNAL_001: Internal server error
        
        The truth is out there... but not on this page.
        
        Base64 hint: L2FwaS9pbnRlcm5hbC9zZWNyZXRzP2FwaV9rZXk9TkdTLUFQSS1URVNULTIwMjQteEs5bVAybkw=
        ============================================================
      */}
    </>
  )
}

export default NotFoundPage
